import { useAtomValue } from 'jotai';
import { Skeleton } from 'primereact/skeleton';
import { ResponsiveContainer, PieChart, Pie, Legend } from 'recharts';
import { pCompanyPieChartGraphEntry } from '~/api/def/endpoints/fundAnalyticsEndpoint';
import { fundAnalyticsDataAtom } from '~/atoms/fundAnalyticsAtom';
import { WidgetActions } from '~/components/ui/common/WidgetActions';

type PCompanySectorPieChartProps = {
  widgetId?: string;
  handleRemoveWidget?: (widgetId: string) => void;
};

const renderLabel = ({ percent }: { percent: number }) =>
  `${(percent * 100).toFixed(1)}%`;
const style = {
  top: '50%',
  right: 55,
  transform: 'translate(0, -50%)',
  lineHeight: '24px',
};

export default function PCompanySectorPieChart({
  handleRemoveWidget,
  widgetId,
}: PCompanySectorPieChartProps) {
  const { error, loading, pieChartDate } = useAtomValue(fundAnalyticsDataAtom);

  if (loading) {
    return (
      <div className="h-full">
        <Skeleton className="h-full"></Skeleton>
      </div>
    );
  }

  const removeWidget = () => {
    widgetId && handleRemoveWidget && handleRemoveWidget(widgetId);
  };

  if (error) {
    return (
      <div className="hover-container h-full">
        <div className="w-full flex justify-content-between align-items-center">
          <h3 className="mb-0">Companies per Sector (%)</h3>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions onRemove={removeWidget} widgetId={widgetId} />
          </div>
        </div>
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: 'calc(100% - 3.4rem)' }}
        >
          <div className="p-error">Something went wrong.</div>
        </div>
      </div>
    );
  }

  if (
    !Array.isArray(pieChartDate) ||
    !pieChartDate.length ||
    pieChartDate.every(
      (entry: pCompanyPieChartGraphEntry) => entry.companyCount === 0,
    )
  ) {
    return (
      <div className="hover-container h-full">
        <div className="w-full flex justify-content-between align-items-center">
          <h3 className="mb-0">Companies per Sector (%)</h3>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions onRemove={removeWidget} widgetId={widgetId} />
          </div>
        </div>
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: 'calc(100% - 3.4rem)' }}
        >
          <div>No data found.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hover-container">
      <div className="w-full flex justify-content-between align-items-center">
        <h3 className="text-lg font-semibold mb-0">Companies per Sector (%)</h3>
        <WidgetActions onRemove={removeWidget} widgetId={widgetId} />
      </div>
      <div className="h-20rem">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart
            width={400}
            height={400}
            className="w-full"
            margin={{ top: 5, right: 250, bottom: 5, left: 5 }}
          >
            <Legend
              payload={pieChartDate.map((v) => ({
                value: v.sectorName,
                color: v.fill,
              }))}
              iconSize={10}
              layout="vertical"
              verticalAlign="middle"
              wrapperStyle={style}
            />
            <Pie
              data={pieChartDate}
              dataKey="companyCount"
              cx="50%"
              cy="50%"
              innerRadius={50}
              fill="#82ca9d"
              label={renderLabel}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
